// PATCH API call is made irrespective of fields changed, to handle the mail validation checks
import { useState, useCallback, useEffect, useRef } from 'react';
import { useDebounce } from '@shared/hooks/useDebounce';
import { useNavigate, useParams, useMatch } from 'react-router-dom';
import { listSubscriptionApiService } from '@/epics/subscriptionManagment/api/listSubscriptionApiService';
import type { SubscriptionPlan } from '@/epics/subscriptionManagment/api/viewSubscriptionPlanApiService';
import type { AddClientDetails, MediaResource } from '../types/addClientType';
import { CLIENT_ERROR_MESSAGES } from '../errors/errorMessages';
import { addClient } from '../api/addClientApiService';
import { finalizeClient } from '../api/finalizeClientApiService';
import {
  editClient,
  type ClientPatchPayload,
} from '../api/editClientApiService';
import { getPresignedUrlsBatch } from '@/epics/technicianManagement/api/presignedUrlBatchApiService';
import { uploadFileToS3 } from '@shared/utils/uploadFileToS3';
import {
  validateName,
  validateEmail,
  validatePhone,
  validateAddress,
  validateCity,
  validateState,
  validateNip,
  validateZip,
  validateBillingRegon,
  validateBillingCompanyName,
  validateBillingNip,
  validateBillingKrs,
  validateInvoiceAddressLine1,
  validateInvoiceZipCode,
  validateInvoiceCity,
} from '../utils/validation';
import { useAddClientStore } from '../store/addClientStore';

type ClientErrors = Omit<AddClientDetails, 'profileFile' | 'contractFiles'> & {
  profileFile?: string;
  contractFiles?: string;
};

const initialFormData: AddClientDetails = {
  name: '',
  email: '',
  phone: '',
  countryCode: '+48',
  currency: 'PLN',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: 'Polska',
  nip: '',
  subscriptionPlan: '',
  paymentStatus: '',
  profileFile: undefined,
  contractFiles: undefined,
  // Invoice details (Dane do faktury)
  billing_company_name: '',
  billing_nip: '',
  billing_regon: '',
  billing_krs: '',
  invoice_address_line_1: '',
  invoice_address_line_2: '',
  invoice_zip_code: '',
  invoice_city: '',
  invoice_province: '',
};

const initialErrors: ClientErrors = {
  name: '',
  email: '',
  phone: '',
  countryCode: '',
  currency: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  nip: '',
  subscriptionPlan: '',
  paymentStatus: '',
  billing_company_name: '',
  billing_nip: '',
  billing_regon: '',
  billing_krs: '',
  invoice_address_line_1: '',
  invoice_address_line_2: '',
  invoice_zip_code: '',
  invoice_city: '',
  invoice_province: '',
};

type StringFormKey = keyof Omit<
  AddClientDetails,
  'profileFile' | 'contractFiles'
>;

// Maps each form field to its PATCH body key and an optional normaliser.
// Fields not listed here are not diffed (files are handled separately).
const FIELD_MAPPINGS: Array<{
  from: StringFormKey;
  to: keyof ClientPatchPayload;
  normalize: (v: string) => string;
}> = [
  { from: 'name', to: 'name', normalize: (v) => v.trim() },
  { from: 'email', to: 'email', normalize: (v) => v.trim().toLowerCase() },
  { from: 'phone', to: 'phoneNumber', normalize: (v) => v.trim() },
  { from: 'countryCode', to: 'countryCode', normalize: (v) => v },
  { from: 'nip', to: 'nip', normalize: (v) => v },
  { from: 'currency', to: 'currency', normalize: (v) => v },
  { from: 'paymentStatus', to: 'paymentStatus', normalize: (v) => v },
  { from: 'subscriptionPlan', to: 'subscriptionPlanId', normalize: (v) => v },
  { from: 'address1', to: 'addressLine1', normalize: (v) => v.trim() },
  { from: 'address2', to: 'addressLine2', normalize: (v) => v.trim() },
  { from: 'city', to: 'city', normalize: (v) => v.trim() },
  { from: 'state', to: 'state', normalize: (v) => v.trim() },
  { from: 'zip', to: 'zipCode', normalize: (v) => v.trim() },
  { from: 'country', to: 'country', normalize: (v) => v },
  // Invoice details (Dane do faktury)
  {
    from: 'billing_company_name',
    to: 'billing_company_name',
    normalize: (v) => v.trim(),
  },
  {
    from: 'billing_nip',
    to: 'billing_nip',
    normalize: (v) => v.replace(/-/g, '').trim(),
  },
  {
    from: 'billing_regon',
    to: 'billing_regon',
    normalize: (v) => v.replace(/\D/g, '').trim(),
  },
  {
    from: 'billing_krs',
    to: 'billing_krs',
    normalize: (v) => v.replace(/\D/g, '').trim(),
  },
  {
    from: 'invoice_address_line_1',
    to: 'invoice_address_line_1',
    normalize: (v) => v.trim(),
  },
  {
    from: 'invoice_address_line_2',
    to: 'invoice_address_line_2',
    normalize: (v) => v.trim(),
  },
  {
    from: 'invoice_zip_code',
    to: 'invoice_zip_code',
    normalize: (v) => v.trim(),
  },
  { from: 'invoice_city', to: 'invoice_city', normalize: (v) => v.trim() },
  {
    from: 'invoice_province',
    to: 'invoice_province',
    normalize: (v) => v.trim(),
  },
];

const buildTextDiff = (
  current: AddClientDetails,
  committed: AddClientDetails
): ClientPatchPayload =>
  FIELD_MAPPINGS.reduce<ClientPatchPayload>((diff, { from, to, normalize }) => {
    const currentVal = normalize(current[from] as string);
    const committedVal = normalize(committed[from] as string);
    return currentVal !== committedVal ? { ...diff, [to]: currentVal } : diff;
  }, {});

export const useAddClient = () => {
  const navigate = useNavigate();
  const { clientId: paramClientId = '' } = useParams<{
    clientId?: string;
  }>();
  const matchStep2 = useMatch('/Klienci/Dodaj-klienta/:clientId/krok-2');
  const matchStep1WithId = useMatch('/Klienci/Dodaj-klienta/:clientId/krok-1');

  const step: 1 | 2 = matchStep2 ? 2 : 1;
  const clientId =
    matchStep2?.params.clientId ??
    matchStep1WithId?.params.clientId ??
    paramClientId;
  const isEditMode = Boolean(clientId);

  const { saveDraft, getDraft, clearDraft } = useAddClientStore();

  const committedFormData = useRef<AddClientDetails | null>(null);

  const [formData, setFormData] = useState<AddClientDetails>(() => {
    if (clientId) {
      const persisted = getDraft(clientId);
      if (persisted) {
        const restored = { ...initialFormData, ...persisted };
        // Seed committedFormData so that buildTextDiff on the first Next click
        // after a page refresh compares against the actual persisted values,
        // not against initialFormData (which would make every field look changed).
        committedFormData.current = restored;
        return restored;
      }
    }
    return initialFormData;
  });

  const [persistedContractDocs, setPersistedContractDocs] = useState<
    MediaResource[]
  >(() => (clientId ? (getDraft(clientId)?.contractDocs ?? []) : []));

  const [profileBase64, setProfileBase64] = useState<string>(() =>
    clientId ? (getDraft(clientId)?.profileBase64 ?? '') : ''
  );

  const [errors, setErrors] = useState<ClientErrors>(initialErrors);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarError, setSnackbarError] = useState('');
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [showList, setShowList] = useState(false);
  const [availablePlans, setAvailablePlans] = useState<
    { id: string; label: string }[]
  >([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [plansPage, setPlansPage] = useState(1);
  const [hasMorePlans, setHasMorePlans] = useState(true);
  const [planSearchQuery, setPlanSearchQuery] = useState('');
  const debouncedPlanSearch = useDebounce(planSearchQuery, 500);
  const [searchResults, setSearchResults] = useState<
    { id: string; label: string }[]
  >([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [selectedPlanLabel, setSelectedPlanLabel] = useState(() =>
    clientId ? (getDraft(clientId)?.subscriptionPlanLabel ?? '') : ''
  );
  const [draftCreated, setDraftCreated] = useState(Boolean(clientId));
  const [createdClientId, setCreatedClientId] = useState('');
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [contractUploadProgress, setContractUploadProgress] = useState<
    Record<string, number>
  >({});
  const [contractFailedFiles, setContractFailedFiles] = useState<string[]>([]);
  const [contractDocResources, setContractDocResources] = useState<
    Record<string, MediaResource>
  >({});

  useEffect(() => {
    if (clientId) {
      setDraftCreated(true);
    }
  }, [clientId]);

  useEffect(() => {
    const fetchFirstPage = async () => {
      setIsLoadingPlans(true);
      try {
        const result = await listSubscriptionApiService.listSubscriptions({
          status: 'active',
          page: 1,
          limit: 10,
        });
        setAvailablePlans(
          result.data.map((plan: SubscriptionPlan) => ({
            id: plan.id,
            label: plan.planName,
          }))
        );
        setHasMorePlans(result.hasNextPage);
        setPlansPage(1);
      } catch (err) {
        console.error('Error fetching subscription plans:', err);
      } finally {
        setIsLoadingPlans(false);
      }
    };
    fetchFirstPage();
  }, []);

  const fetchMorePlans = useCallback(async () => {
    if (isLoadingPlans || !hasMorePlans) {
      return;
    }
    setIsLoadingPlans(true);
    const nextPage = plansPage + 1;
    try {
      const result = await listSubscriptionApiService.listSubscriptions({
        status: 'active',
        page: nextPage,
        limit: 10,
      });
      setAvailablePlans((prev) => {
        const newItems = result.data
          .map((plan: SubscriptionPlan) => ({
            id: plan.id,
            label: plan.planName,
          }))
          .filter((newItem) => !prev.some((item) => item.id === newItem.id));
        return [...prev, ...newItems];
      });
      setHasMorePlans(result.hasNextPage);
      setPlansPage(result.page);
    } catch (err) {
      console.error('Error fetching more subscription plans:', err);
    } finally {
      setIsLoadingPlans(false);
    }
  }, [isLoadingPlans, hasMorePlans, plansPage]);

  useEffect(() => {
    if (!debouncedPlanSearch) {
      setSearchResults([]);
      return;
    }
    const fetchSearchResults = async () => {
      setIsSearchLoading(true);
      try {
        const result = await listSubscriptionApiService.listSubscriptions({
          status: 'active',
          search: debouncedPlanSearch,
          page: 1,
          limit: 10,
        });
        setSearchResults(
          result.data.map((plan: SubscriptionPlan) => ({
            id: plan.id,
            label: plan.planName,
          }))
        );
      } catch (err) {
        console.error('Error searching subscription plans:', err);
      } finally {
        setIsSearchLoading(false);
      }
    };
    fetchSearchResults();
  }, [debouncedPlanSearch]);

  const handlePlanSearchChange = useCallback((query: string) => {
    setPlanSearchQuery(query);
  }, []);

  const invalidateDraft = useCallback(() => {
    setDraftCreated(false);
    setCreatedClientId('');
  }, []);

  const uploadContractFile = useCallback(async (file: File): Promise<void> => {
    const key = file.name;
    setContractUploadProgress((prev) => ({ ...prev, [key]: 0 }));
    setContractFailedFiles((prev) => prev.filter((f) => f !== key));

    try {
      const presignedResponse = await getPresignedUrlsBatch({
        paths: [
          `clients/entityid-placeholder/docs/resourceid-placeholder/${file.name}`,
        ],
        generatePath: true,
      });

      const fileData = presignedResponse.files[0];
      if (!fileData) {
        throw new Error(CLIENT_ERROR_MESSAGES.UNEXPECTED_ERROR);
      }

      await uploadFileToS3(fileData.url, file, (bytesLoaded) => {
        setContractUploadProgress((prev) => ({ ...prev, [key]: bytesLoaded }));
      });

      const resource: MediaResource = {
        path: fileData.path,
        resourceId: fileData.resourceId,
        isUploaded: true,
        filename: file.name,
        fileType: file.type,
        fileSize: file.size,
      };

      setContractDocResources((prev) => ({ ...prev, [key]: resource }));
      setContractUploadProgress((prev) => {
        const { [key]: _removed, ...rest } = prev;
        return rest;
      });
    } catch {
      setContractUploadProgress((prev) => {
        const { [key]: _removed, ...rest } = prev;
        return rest;
      });
      setContractFailedFiles((prev) => [...prev.filter((f) => f !== key), key]);
    }
  }, []);

  const retryContractUpload = useCallback(
    (filename: string): void => {
      const file = formData.contractFiles?.find((f) => f.name === filename);
      if (file) {
        void uploadContractFile(file);
      }
    },
    [formData.contractFiles, uploadContractFile]
  );

  const makeFieldHandler = useCallback(
    (field: keyof ClientErrors, trim = false) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = trim ? e.target.value.trim() : e.target.value.trimStart();
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
          setErrors((prev) => ({ ...prev, [field]: '' }));
        }
        invalidateDraft();
      },
    [errors, invalidateDraft]
  );

  const handleNameChange = makeFieldHandler('name');
  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.replace(/\s/g, '');
      setFormData((prev) => ({ ...prev, email: value }));
      if (errors.email) {
        setErrors((prev) => ({ ...prev, email: '' }));
      }
      invalidateDraft();
    },
    [errors.email, invalidateDraft]
  );
  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value.trim().slice(0, 9);
      setFormData((prev) => ({ ...prev, phone: value }));
      if (errors.phone) {
        setErrors((prev) => ({ ...prev, phone: '' }));
      }
      invalidateDraft();
    },
    [errors.phone, invalidateDraft]
  );
  const handleAddress1Change = makeFieldHandler('address1');
  const handleAddress2Change = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({
        ...prev,
        address2: e.target.value.trimStart(),
      }));
      invalidateDraft();
    },
    [invalidateDraft]
  );
  const handleCityChange = makeFieldHandler('city', true);
  const handleStateChange = makeFieldHandler('state');
  const handleZipChange = makeFieldHandler('zip', true);
  const handleNipChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
      let formatted = digits;
      if (digits.length > 8) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
      } else if (digits.length > 6) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else if (digits.length > 3) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      }
      setFormData((prev) => ({ ...prev, nip: formatted }));
      if (errors.nip) {
        setErrors((prev) => ({ ...prev, nip: '' }));
      }
      invalidateDraft();
    },
    [errors.nip, invalidateDraft]
  );

  // Invoice details handlers
  const handleBillingCompanyNameChange = makeFieldHandler(
    'billing_company_name'
  );
  const handleBillingNipChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
      let formatted = digits;
      if (digits.length > 8) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
      } else if (digits.length > 6) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else if (digits.length > 3) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      }
      setFormData((prev) => ({ ...prev, billing_nip: formatted }));
      if (errors.billing_nip) {
        setErrors((prev) => ({ ...prev, billing_nip: '' }));
      }
      invalidateDraft();
    },
    [errors.billing_nip, invalidateDraft]
  );
  const handleBillingRegonChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 14);
      setFormData((prev) => ({ ...prev, billing_regon: digits }));
      if (errors.billing_regon) {
        setErrors((prev) => ({ ...prev, billing_regon: '' }));
      }
      invalidateDraft();
    },
    [errors.billing_regon, invalidateDraft]
  );
  const handleBillingKrsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
      setFormData((prev) => ({ ...prev, billing_krs: digits }));
      if (errors.billing_krs) {
        setErrors((prev) => ({ ...prev, billing_krs: '' }));
      }
      invalidateDraft();
    },
    [errors.billing_krs, invalidateDraft]
  );
  const handleInvoiceAddressLine1Change = makeFieldHandler(
    'invoice_address_line_1'
  );
  const handleInvoiceAddressLine2Change = makeFieldHandler(
    'invoice_address_line_2'
  );
  const handleInvoiceZipCodeChange = makeFieldHandler('invoice_zip_code', true);
  const handleInvoiceCityChange = makeFieldHandler('invoice_city');
  const handleInvoiceProvinceChange = makeFieldHandler('invoice_province');

  const handleCurrencySelect = useCallback(
    (id: string) => {
      setFormData((prev) => ({ ...prev, currency: id }));
      setErrors((prev) => ({ ...prev, currency: '' }));
      invalidateDraft();
    },
    [invalidateDraft]
  );

  const handleSubscriptionPlanSelect = useCallback(
    (id: string) => {
      const displayedPlans = planSearchQuery ? searchResults : availablePlans;
      const label = displayedPlans.find((p) => p.id === id)?.label ?? '';
      setSelectedPlanLabel(label);
      setFormData((prev) => ({ ...prev, subscriptionPlan: id }));
      setErrors((prev) => ({ ...prev, subscriptionPlan: '' }));
      invalidateDraft();
    },
    [invalidateDraft, planSearchQuery, searchResults, availablePlans]
  );

  const handlePaymentStatusSelect = useCallback(
    (id: string) => {
      setFormData((prev) => ({ ...prev, paymentStatus: id }));
      setErrors((prev) => ({ ...prev, paymentStatus: '' }));
      invalidateDraft();
    },
    [invalidateDraft]
  );

  const handleProfileFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      setFormData((prev) => ({ ...prev, profileFile: file }));
      if (file) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          setProfileBase64((ev.target?.result as string) ?? '');
        };
        reader.readAsDataURL(file);
      } else {
        setProfileBase64('');
      }
      invalidateDraft();
    },
    [invalidateDraft]
  );

  const handleProfileFileDelete = useCallback(() => {
    setFormData((prev) => ({ ...prev, profileFile: undefined }));
    setProfileBase64('');
    const id = clientId || createdClientId;
    if (id) {
      const draft = getDraft(id);
      if (draft) {
        const {
          profileResource: _r,
          profileBase64: _b,
          ...draftWithoutProfile
        } = draft;
        saveDraft(id, draftWithoutProfile);
      }
    }
    invalidateDraft();
  }, [invalidateDraft, clientId, createdClientId, getDraft, saveDraft]);

  const handleProfileFileError = useCallback((message: string) => {
    setSnackbarError(message);
    setIsSnackbarOpen(true);
  }, []);

  const handleContractFilesError = useCallback((message: string) => {
    setErrors((prev) => ({ ...prev, contractFiles: message }));
    setTimeout(() => {
      setErrors((prev) => ({ ...prev, contractFiles: '' }));
    }, 3000);
  }, []);

  const handleContractFilesSelect = useCallback(
    (files: File[]) => {
      setFormData((prev) => ({ ...prev, contractFiles: files }));
      if (files.length > 0) {
        setErrors((prev) => ({ ...prev, contractFiles: '' }));
      }
      invalidateDraft();

      // Clear resources for removed files
      setContractDocResources((prev) => {
        const kept: Record<string, MediaResource> = {};
        for (const f of files) {
          const existing = prev[f.name];
          if (existing) {
            kept[f.name] = existing;
          }
        }
        return kept;
      });
      setContractFailedFiles((prev) =>
        prev.filter((name) => files.some((f) => f.name === name))
      );

      // Upload only new files (not already uploaded or in progress)
      for (const file of files) {
        if (
          !contractDocResources[file.name] &&
          contractUploadProgress[file.name] === undefined
        ) {
          void uploadContractFile(file);
        }
      }
    },
    [
      invalidateDraft,
      contractDocResources,
      contractUploadProgress,
      uploadContractFile,
    ]
  );

  const validateStep1 = useCallback((): boolean => {
    const hasContracts =
      (formData.contractFiles && formData.contractFiles.length > 0) ||
      persistedContractDocs.length > 0;
    const validationErrors: ClientErrors = {
      ...initialErrors,
      name: validateName(formData.name),
      email: validateEmail(formData.email),
      phone: validatePhone(formData.phone),
      address1: validateAddress(formData.address1),
      city: validateCity(formData.city),
      state: validateState(formData.state),
      zip: validateZip(formData.zip),
      nip: validateNip(formData.nip),
      billing_company_name: validateBillingCompanyName(
        formData.billing_company_name
      ),
      billing_nip: validateBillingNip(formData.billing_nip),
      billing_regon: validateBillingRegon(formData.billing_regon),
      billing_krs: validateBillingKrs(formData.billing_krs),
      invoice_address_line_1: validateInvoiceAddressLine1(
        formData.invoice_address_line_1
      ),
      invoice_zip_code: validateInvoiceZipCode(formData.invoice_zip_code),
      invoice_city: validateInvoiceCity(formData.invoice_city),
      subscriptionPlan: !formData.subscriptionPlan
        ? CLIENT_ERROR_MESSAGES.REQUIRED
        : '',
      paymentStatus: !formData.paymentStatus
        ? CLIENT_ERROR_MESSAGES.REQUIRED
        : '',
      contractFiles: !hasContracts ? CLIENT_ERROR_MESSAGES.REQUIRED : '',
    };
    setErrors(validationErrors);
    return !Object.values(validationErrors).some((e) => e !== '');
  }, [formData, persistedContractDocs]);

  // Uploads any new files and returns their MediaResource objects.
  // Only processes files whose reference has changed vs the committed snapshot.
  const uploadChangedFiles = useCallback(
    async (
      committed: AddClientDetails
    ): Promise<{
      profile: MediaResource | undefined;
      docs: MediaResource[];
    }> => {
      const profileChanged =
        formData.profileFile !== committed.profileFile &&
        Boolean(formData.profileFile);
      const contractsChanged =
        formData.contractFiles !== committed.contractFiles &&
        Boolean(formData.contractFiles?.length);

      if (!profileChanged && !contractsChanged) {
        return { profile: undefined, docs: [] };
      }

      const paths: string[] = [];

      if (profileChanged && formData.profileFile) {
        paths.push(
          `clients/entityid-placeholder/profiles/resourceid-placeholder/${formData.profileFile.name}`
        );
      }

      if (contractsChanged && formData.contractFiles) {
        formData.contractFiles.forEach((file) => {
          paths.push(
            `clients/entityid-placeholder/docs/resourceid-placeholder/${file.name}`
          );
        });
      }

      const presignedResponse = await getPresignedUrlsBatch({
        paths,
        generatePath: true,
      });

      let fileIndex = 0;
      let profile: MediaResource | undefined;
      const docs: MediaResource[] = [];

      if (profileChanged && formData.profileFile) {
        const fileData = presignedResponse.files[fileIndex];
        if (fileData) {
          await uploadFileToS3(fileData.url, formData.profileFile);
          profile = {
            path: fileData.path,
            resourceId: fileData.resourceId,
            isUploaded: true,
            filename: formData.profileFile.name,
            fileType: formData.profileFile.type,
            fileSize: formData.profileFile.size,
          };
          fileIndex++;
        }
      }

      if (contractsChanged && formData.contractFiles) {
        for (const contractFile of formData.contractFiles) {
          const existing = contractDocResources[contractFile.name];
          if (existing) {
            docs.push(existing);
          } else {
            const fileData = presignedResponse.files[fileIndex];
            if (fileData) {
              await uploadFileToS3(fileData.url, contractFile);
              docs.push({
                path: fileData.path,
                resourceId: fileData.resourceId,
                isUploaded: true,
                filename: contractFile.name,
                fileType: contractFile.type,
                fileSize: contractFile.size,
              });
              fileIndex++;
            }
          }
        }
      }

      return { profile, docs };
    },
    [formData, contractDocResources]
  );

  const processStep1 = useCallback(async () => {
    if (!validateStep1()) {
      return;
    }

    if (
      Object.keys(contractUploadProgress).length > 0 ||
      contractFailedFiles.length > 0
    ) {
      setSnackbarError(CLIENT_ERROR_MESSAGES.UNEXPECTED_ERROR);
      setIsSnackbarOpen(true);
      return;
    }

    setIsSubmitting(true);
    try {
      // ── PATCH mode: returning from step 2 ────────────────────────────
      if (isEditMode) {
        const committed = committedFormData.current ?? initialFormData;
        const textDiff = buildTextDiff(formData, committed);
        const { profile, docs } = await uploadChangedFiles(committed);

        // Always call PATCH and always include email in the payload.
        // If no other fields changed, only email is sent.
        // If fields changed, the diff is spread on top — if email itself
        // changed, textDiff already contains the updated value and overwrites
        // the base email entry, so there is no duplication.
        const patchPayload: ClientPatchPayload = {
          email: formData.email.trim().toLowerCase(),
          ...textDiff,
          ...(profile ? { profile } : {}),
          ...(docs.length > 0 ? { docs } : {}),
        };

        const result = await editClient(clientId, patchPayload);

        if (result.success) {
          committedFormData.current = { ...formData };
          const updatedDocs = docs.length > 0 ? docs : persistedContractDocs;
          const savedProfileResource =
            profile ?? getDraft(clientId)?.profileResource;
          const savedProfileBase64 =
            profileBase64 || getDraft(clientId)?.profileBase64 || '';
          setPersistedContractDocs(updatedDocs);
          setFormData((prev) => ({ ...prev, contractFiles: undefined }));
          saveDraft(clientId, {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            countryCode: formData.countryCode,
            currency: formData.currency,
            address1: formData.address1,
            address2: formData.address2,
            city: formData.city,
            state: formData.state,
            zip: formData.zip,
            country: formData.country,
            nip: formData.nip,
            subscriptionPlan: formData.subscriptionPlan,
            subscriptionPlanLabel: selectedPlanLabel,
            paymentStatus: formData.paymentStatus,
            billing_company_name: formData.billing_company_name,
            billing_nip: formData.billing_nip,
            billing_regon: formData.billing_regon,
            billing_krs: formData.billing_krs,
            invoice_address_line_1: formData.invoice_address_line_1,
            invoice_address_line_2: formData.invoice_address_line_2,
            invoice_zip_code: formData.invoice_zip_code,
            invoice_city: formData.invoice_city,
            invoice_province: formData.invoice_province,
            contractDocs: updatedDocs,
            ...(savedProfileResource
              ? { profileResource: savedProfileResource }
              : {}),
            ...(savedProfileBase64
              ? { profileBase64: savedProfileBase64 }
              : {}),
          });
          setDraftCreated(true);
          navigate(`/Klienci/Dodaj-klienta/${clientId}/krok-2`);
        } else {
          if (result.error === CLIENT_ERROR_MESSAGES.EMAIL_ALREADY_EXISTS) {
            setErrors((prev) => ({ ...prev, email: result.error || '' }));
          } else {
            setSnackbarError(
              result.error || CLIENT_ERROR_MESSAGES.DEFAULT_ERROR
            );
            setIsSnackbarOpen(true);
          }
        }
        return;
      }

      // ── POST mode: first submission ───────────────────────────────────
      let entityId: string | undefined;
      let profile: MediaResource | undefined;

      // Contract docs are already uploaded eagerly — collect resolved resources
      const docs: MediaResource[] = (formData.contractFiles ?? [])
        .map((f) => contractDocResources[f.name])
        .filter((r): r is MediaResource => r !== undefined);

      if (formData.profileFile) {
        const presignedResponse = await getPresignedUrlsBatch({
          paths: [
            `clients/entityid-placeholder/profiles/resourceid-placeholder/${formData.profileFile.name}`,
          ],
          generatePath: true,
        });

        entityId = presignedResponse.entityId;
        const fileData = presignedResponse.files[0];
        if (fileData) {
          await uploadFileToS3(fileData.url, formData.profileFile);
          profile = {
            path: fileData.path,
            resourceId: fileData.resourceId,
            isUploaded: true,
            filename: formData.profileFile.name,
            fileType: formData.profileFile.type,
            fileSize: formData.profileFile.size,
          };
        }
      }

      const result = await addClient(formData, entityId, profile, docs);

      if (result.success) {
        const data = result.data as { client: { id: string } };
        const newClientId = data?.client?.id ?? '';
        committedFormData.current = { ...formData };
        setPersistedContractDocs(docs);
        setFormData((prev) => ({ ...prev, contractFiles: undefined }));
        saveDraft(newClientId, {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          countryCode: formData.countryCode,
          currency: formData.currency,
          address1: formData.address1,
          address2: formData.address2,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          country: formData.country,
          nip: formData.nip,
          subscriptionPlan: formData.subscriptionPlan,
          subscriptionPlanLabel: selectedPlanLabel,
          paymentStatus: formData.paymentStatus,
          billing_company_name: formData.billing_company_name,
          billing_nip: formData.billing_nip,
          billing_regon: formData.billing_regon,
          billing_krs: formData.billing_krs,
          invoice_address_line_1: formData.invoice_address_line_1,
          invoice_address_line_2: formData.invoice_address_line_2,
          invoice_zip_code: formData.invoice_zip_code,
          invoice_city: formData.invoice_city,
          invoice_province: formData.invoice_province,
          contractDocs: docs,
          ...(profile ? { profileResource: profile } : {}),
          ...(profileBase64 ? { profileBase64 } : {}),
        });
        setCreatedClientId(newClientId);
        setDraftCreated(true);
        navigate(`/Klienci/Dodaj-klienta/${newClientId}/krok-2`);
      } else {
        if (result.error === CLIENT_ERROR_MESSAGES.EMAIL_ALREADY_EXISTS) {
          setErrors((prev) => ({ ...prev, email: result.error || '' }));
        } else {
          setSnackbarError(result.error || CLIENT_ERROR_MESSAGES.DEFAULT_ERROR);
          setIsSnackbarOpen(true);
        }
      }
    } catch (_error) {
      setSnackbarError(CLIENT_ERROR_MESSAGES.UNEXPECTED_ERROR);
      setIsSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    formData,
    profileBase64,
    selectedPlanLabel,
    validateStep1,
    navigate,
    isEditMode,
    clientId,
    uploadChangedFiles,
    contractDocResources,
    contractFailedFiles,
    contractUploadProgress,
    saveDraft,
    getDraft,
    persistedContractDocs,
  ]);

  const handleStep2Finalize = useCallback(async () => {
    setIsFinalizing(true);
    try {
      const result = await finalizeClient(clientId || createdClientId);
      if (result.success) {
        const successMessage =
          formData.paymentStatus === 'paid'
            ? 'Klient został dodany. Na jego skrzynkę wysłano e-mail z tymczasowymi danymi logowania.'
            : 'Klient został dodany. Dostęp zostanie przyznany po oznaczeniu płatności jako Opłacone.';
        clearDraft(clientId || createdClientId);
        navigate('/Klienci', { replace: true, state: { successMessage } });
      } else {
        setSnackbarError(result.error || CLIENT_ERROR_MESSAGES.DEFAULT_ERROR);
        setIsSnackbarOpen(true);
        setIsFinalizing(false);
      }
    } catch {
      setSnackbarError(CLIENT_ERROR_MESSAGES.UNEXPECTED_ERROR);
      setIsSnackbarOpen(true);
      setIsFinalizing(false);
    }
  }, [clientId, createdClientId, formData.paymentStatus, navigate, clearDraft]);

  const handleForwardToStep2 = useCallback(async () => {
    await processStep1();
  }, [processStep1]);

  const handleStep2Submit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      navigate('/Klienci', {
        replace: true,
        state: { showAddClientSuccess: true },
      });
    },
    [navigate]
  );

  const handleSubmit = step === 1 ? handleForwardToStep2 : handleStep2Submit;
  const isAddClientEnabled = Boolean(clientId || createdClientId);

  // Step 1 cancel skips the confirmation modal only when nothing has been
  // entered or persisted yet. On step 2 the modal always shows because step 1
  // has already been submitted, so a server-side draft exists.
  const handleCancel = useCallback(() => {
    if (step === 2) {
      setIsCancelModalOpen(true);
      return;
    }

    const hasServerDraft = Boolean(clientId || createdClientId || draftCreated);
    const hasFiles = Boolean(
      formData.profileFile ||
        (formData.contractFiles && formData.contractFiles.length > 0) ||
        persistedContractDocs.length > 0 ||
        profileBase64
    );
    const baseline = committedFormData.current ?? initialFormData;
    const hasFieldEdits =
      Object.keys(buildTextDiff(formData, baseline)).length > 0;

    if (hasServerDraft || hasFiles || hasFieldEdits) {
      setIsCancelModalOpen(true);
      return;
    }

    navigate('/Klienci');
  }, [
    step,
    clientId,
    createdClientId,
    draftCreated,
    formData,
    persistedContractDocs,
    profileBase64,
    navigate,
  ]);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleConfirmCancel = useCallback(() => {
    clearDraft(clientId || createdClientId);
    setFormData(initialFormData);
    setPersistedContractDocs([]);
    setErrors(initialErrors);
    setIsSnackbarOpen(false);
    setSnackbarError('');
    setIsCancelModalOpen(false);
    navigate('/Klienci');
  }, [clearDraft, clientId, createdClientId, navigate]);

  const handleExistingContractDelete = useCallback(
    (path: string) => {
      setPersistedContractDocs((prev) => {
        const updated = prev.filter((doc) => doc.path !== path);
        const id = clientId || createdClientId;
        if (id) {
          const draft = getDraft(id);
          if (draft) {
            saveDraft(id, { ...draft, contractDocs: updated });
          }
        }
        return updated;
      });
    },
    [clientId, createdClientId, getDraft, saveDraft]
  );

  const handleCloseSnackbar = useCallback(() => {
    setIsSnackbarOpen(false);
    setSnackbarError('');
  }, []);

  const handleBackToStep1 = useCallback(() => {
    if (clientId) {
      navigate(`/Klienci/Dodaj-klienta/${clientId}/krok-1`);
    } else {
      navigate('/Klienci/Dodaj-klienta/krok-1');
    }
  }, [navigate, clientId]);

  return {
    step,
    clientId,
    formData,
    setFormData,
    errors,
    isSubmitting,
    snackbarError,
    isSnackbarOpen,
    isCancelModalOpen,
    handleNameChange,
    handleEmailChange,
    handlePhoneChange,
    handleAddress1Change,
    handleAddress2Change,
    handleCityChange,
    handleStateChange,
    handleZipChange,
    handleNipChange,
    handleBillingCompanyNameChange,
    handleBillingNipChange,
    handleBillingRegonChange,
    handleBillingKrsChange,
    handleInvoiceAddressLine1Change,
    handleInvoiceAddressLine2Change,
    handleInvoiceZipCodeChange,
    handleInvoiceCityChange,
    handleInvoiceProvinceChange,
    handleCurrencySelect,
    handleSubscriptionPlanSelect,
    selectedPlanLabel,
    handlePaymentStatusSelect,
    handleProfileFileChange,
    handleProfileFileDelete,
    handleProfileFileError,
    handleContractFilesError,
    handleContractFilesSelect,
    handleSubmit,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,
    handleBackToStep1,
    availablePlans: planSearchQuery ? searchResults : availablePlans,
    isLoadingPlans: planSearchQuery ? isSearchLoading : isLoadingPlans,
    hasMorePlans: planSearchQuery ? false : hasMorePlans,
    fetchMorePlans,
    handlePlanSearchChange,
    isPlanSearchActive: Boolean(planSearchQuery),
    planSearchQuery,
    isSearchLoading,
    showList,
    setShowList,
    draftCreated,
    isFinalizing,
    createdClientId,
    handleStep2Finalize,
    handleForwardToStep2,
    isAddClientEnabled,
    persistedContractDocs,
    handleExistingContractDelete,
    contractUploadProgress,
    contractFailedFiles,
    retryContractUpload,
    profileDisplayUrl: profileBase64,
  };
};
